import { useEffect, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import { User } from '../types/auth'

interface UseAuthRedirectParams {
  loading: boolean
  user: User | null
  hasSubscription: boolean
  isTrial: boolean
  pathname: string
  navigate: NavigateFunction
}

export function useAuthRedirect({
  loading,
  user,
  hasSubscription,
  isTrial,
  pathname,
  navigate
}: UseAuthRedirectParams): void {
  const [redirectInProgress, setRedirectInProgress] = useState(false)

  useEffect(() => {
    // Only perform redirection if we're fully loaded and not already redirecting
    if (!loading && user && !redirectInProgress) {
      const currentPath = pathname

      // Special exception for pricing page to allow upgrade flow
      if (currentPath === '/pricing') {
        return // Don't redirect away from pricing
      }

      // Special exception for affiliate portal
      if (currentPath === '/affiliate-portal') {
        return // Don't redirect away from affiliate portal
      }

      // Exception for password reset/set pages to prevent redirect during recovery flow
      if (currentPath === '/reset-password' || currentPath === '/set-password') {
        return
      }

      // Handle the case where user is authenticated but not on an app route
      if ((hasSubscription || isTrial) && !currentPath.startsWith('/app/')) {
        setRedirectInProgress(true)
        navigate('/app/calendar')
        // Reset redirect flag after navigation
        setTimeout(() => setRedirectInProgress(false), 100)
      }

      // Handle the case where user has no access but is on a protected route
      if (!hasSubscription && !isTrial && currentPath.startsWith('/app/')) {
        setRedirectInProgress(true)
        navigate('/pricing')
        // Reset redirect flag after navigation
        setTimeout(() => setRedirectInProgress(false), 100)
      }
    }
  }, [loading, user, hasSubscription, isTrial, pathname, navigate, redirectInProgress])
}
